import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { withTheme } from 'styled-components';
import { SVGIcon, MaterialIcon } from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { FileTypes, FileMimeTypes } from 'constants/enums';
import {
  DropzoneContainer,
  DragAreaText,
  DragAreaInput,
  DragAreaLink,
  DragAreaDiv,
  ErrorInfo,
  DroppedFileName,
  FileIcon,
  CancelButton,
  FileNameWrapper,
} from './styles';
import messages from './messages';

const ImportFileDropzone = ({
  fieldName,
  setFieldValue,
  disabled,
  error,
  supportType = [FileTypes.EPUB],
}) => {
  const messagePostFix = `${supportType.reduce((accumulator, value) => `${accumulator}_${value}`, '')}`;
  const initialDragState = {
    isDragReject: false,
    isDragAccept: false,
    isDragActive: false,
  };
  const [fileName, setFileName] = useState('');
  const [dragState, setDragState] = useState(initialDragState);

  const onDrop = useCallback(
    (acceptedFiles = []) => {
      if(acceptedFiles.length){
        setDragState(initialDragState);
        // Sets the appropriate value to the form object
        setFieldValue(fieldName, acceptedFiles[0]);
        // uploading only one file at a time
        setFileName(acceptedFiles[0].name);
      } else {
        setDragState({...initialDragState, isDragReject: true});
        setFieldValue(fieldName, undefined);
        setFileName('');
      }
    },
    [initialDragState, setFieldValue, fieldName]
  );

  const onDragEnter = useCallback(()=>{ setDragState({ ...initialDragState, isDragActive: true });},[initialDragState]);
  const onDragLeave = useCallback(()=>{ setDragState({ ...initialDragState }); },[initialDragState]);

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    open,
  } = useDropzone({
    noClick: true,
    accept: `${supportType.map((type) => FileMimeTypes[type]).join()}`,
    onDrop,
    onDragEnter,
    onDragLeave,
    multiple: false,
    disabled,
  });
  const removeFile = () => {
    acceptedFiles.length = 0;
    // Reset the values
    setDragState({...initialDragState});
    setFieldValue(fieldName, undefined);
    setFileName('');
  };

  const acceptedFilesLength = acceptedFiles.length;

  const droppedFileName = (
    <DroppedFileName>
      <FileNameWrapper>
        <span>
          <FileIcon icon="duotoneFile"></FileIcon>
        </span>
        {fileName}
      </FileNameWrapper>
      <CancelButton grey iconOnly disabled={disabled} onClick={removeFile}>
        <MaterialIcon id="delete" />
      </CancelButton>
    </DroppedFileName>
  );

  const dragAreaInfo = (
    <DragAreaDiv>
      <DragAreaText>
        {intl.formatMessage(messages.dragAndDropUploadMessage)}
      </DragAreaText>
      <DragAreaText>{intl.formatMessage(messages.or)}</DragAreaText>
      <DragAreaLink type="button" onClick={open}>
        {intl.formatMessage(messages.browseFile)}
      </DragAreaLink>
    </DragAreaDiv>
  );
  const {isDragReject, isDragAccept, isDragActive} = dragState;
  return (
    <>
      <DropzoneContainer
        error={error}
        isDragReject={isDragReject}
        isDragAccept={isDragAccept}
        isDragActive={isDragActive}
        acceptedFilesLength={acceptedFilesLength}
        {...getRootProps({
          className: `dropzone ${disabled ? 'disabled' : ''}`,
        })}
      >
        <DragAreaInput {...getInputProps()} />
        {!acceptedFilesLength &&
          (!isDragActive || isDragReject) &&
          dragAreaInfo}
        {!!acceptedFilesLength && droppedFileName}
      </DropzoneContainer>
      <ErrorInfo>
        {(error || isDragReject) && <SVGIcon icon="warningCircle"></SVGIcon>}
        {isDragReject ? intl.formatMessage(messages[`wrongFileFormat${messagePostFix}`]) : error}
      </ErrorInfo>
    </>
  );
};

ImportFileDropzone.propTypes = {
  fieldName: PropTypes.string,
  setFieldValue: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  supportType: PropTypes.array,
};
export default withTheme(ImportFileDropzone);
